import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import WrapperSeamless from '../../layout-components/WrapperSeamless';
import Table from '../../components/tables/table';
import * as Api from '../../service';
import useStore from '../../context';

const getLogUrl = (log) =>
  log.url || log.path || log.requestedUrl || log.fromUrl || '-';
const getLogCount = (log) =>
  log.hitCount ?? log.count ?? log.hits ?? log.viewCount ?? log.total ?? '-';
const getLogLast = (log) =>
  log.lastSeenAt || log.lastSeen || log.lastHitAt || log.updatedAt || '-';
const getLogFirst = (log) =>
  log.firstSeenAt || log.firstSeen || log.createdAt || '-';

const SeoNotFoundLog = () => {
  const { setErrorMsg, setSuccessMsg } = useStore();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [redirectDrafts, setRedirectDrafts] = useState({});

  const loadLogs = useCallback(async () => {
    try {
      const res = await Api.seo.get404Logs();
      const items = Array.isArray(res) ? res : res?.items || res?.data || [];
      setLogs(items);
      setTotal(res?.total ?? res?.totalCount ?? items.length);
    } catch (e) {
      setErrorMsg(e.message);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, []);

  const fields = useMemo(
    () => [
      <th className="text-left px-4" width="260">URL</th>,
      <th className="text-left">Hits</th>,
      <th className="text-left">Last Seen</th>,
      <th className="text-left">First Seen</th>,
      <th className="text-left">Redirect To</th>,
      <th className="text-left">Status</th>
    ],
    []
  );

  const updateDraft = (key, updates) => {
    setRedirectDrafts((st) => ({
      ...st,
      [key]: {
        toUrl: '',
        statusCode: 301,
        notes: '',
        ...st[key],
        ...updates
      }
    }));
  };

  const createRedirect = async (log) => {
    const fromUrl = getLogUrl(log);
    const key = log.id || log._id || fromUrl;
    const draft = redirectDrafts[key] || { toUrl: '', statusCode: 301 };
    if (!draft.toUrl) return setErrorMsg('toUrl is required');
    try {
      await Api.seo.createRedirect(
        JSON.stringify({
          fromUrl,
          toUrl: draft.toUrl,
          statusCode: draft.statusCode,
          notes: draft.notes
        })
      );
      setSuccessMsg('redirect created');
      updateDraft(key, { toUrl: '', notes: '', statusCode: 301 });
      loadLogs();
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const rows = logs?.map((log) => {
    const fromUrl = getLogUrl(log);
    const key = log.id || log._id || fromUrl;
    const draft = redirectDrafts[key] || { toUrl: '', statusCode: 301, notes: '' };
    return (
    <>
      <td className="text-left">
        <div className="font-size-sm">{fromUrl}</div>
      </td>
      <td className="text-left">
        <div className="font-size-sm">{getLogCount(log)}</div>
      </td>
      <td className="text-left">
        <div className="font-size-sm">{getLogLast(log)}</div>
      </td>
      <td className="text-left">
        <div className="font-size-sm">{getLogFirst(log)}</div>
      </td>
      <td className="text-left">
        <TextField
          variant="outlined"
          size="small"
          placeholder="/new-url"
          value={draft.toUrl}
          onChange={(e) => updateDraft(key, { toUrl: e.target.value })}
        />
      </td>
      <td className="text-left">
        <FormControl variant="outlined" size="small">
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={draft.statusCode}
            onChange={(e) =>
              updateDraft(key, { statusCode: Number(e.target.value) })
            }
          >
            <MenuItem value={301}>301</MenuItem>
            <MenuItem value={302}>302</MenuItem>
          </Select>
        </FormControl>
      </td>
      <td className="text-right">
        <Button
          className="btn-neutral-success"
          onClick={() => createRedirect(log)}
        >
          Add Redirect
        </Button>
      </td>
    </>
  )});

  return (
    <Container>
      <WrapperSeamless sectionHeading="404 Log">
        <Table
          title="404 Log"
          noDrag
          items={logs}
          setItems={setLogs}
          total={total}
          fields={fields}
          rows={rows}
          actions
        />
      </WrapperSeamless>
    </Container>
  );
};

export default SeoNotFoundLog;
